<template>
  <MainLayout>
    <!-- Menu Sub Navigation -->
    <MenuSubNavbar />
    <div class="px-6">
      <!-- Categories Content -->
      <PageHeader
        :title="t('app.views.menu.categories.title') || 'Categories'"
        :subtitle="t('app.views.menu.categories.subtitle') || 'Manage your menu categories'"
      >
        <template #actions>
          <div class="flex items-center gap-3">
            <!-- View Mode Toggle -->
            <ViewModeToggle v-model:view-mode="viewMode" />
            
            <!-- Add button - Cloud only (hidden in Electron Desktop) -->
            <BaseButton
              v-if="platformFeatures.cloudOnly.categoryManagement && canEditCategories"
              @click="openModal()"
            >
              {{ t('app.views.menu.categories.add') || 'Add Category' }}
            </BaseButton>
          </div>
        </template>
      </PageHeader>
      
      <div class="mt-4 px-1 sm:px-0 flex justify-end">
        <div class="w-full sm:max-w-xs">
          <SearchBar
            :model-value="categorySearch"
            :placeholder="t('app.actions.search') || 'Buscar categorías'"
            input-id="category-search"
            @update:model-value="setCategorySearch($event)"
          />
        </div>
      </div>

      <!-- Category List -->
      <CategoryList
        :categories="menuCategoriesDetailed"
        :loading="categoriesLoading"
        :can-edit="canEditCategories"
        :empty-message="categorySearch.trim() ? (t('app.common.no_results') || 'No se encontraron categorías') : (t('app.views.menu.categories.empty') || 'No categories yet')"
        :view-mode="viewMode"
        @edit="openModal"
        @delete="deleteCategory"
      />

      <!-- Category Modal -->
      <CategoryModal
        v-model:name="categoryFormName"
        v-model:description="categoryFormDescription"
        v-model:visible-in-kitchen="categoryFormVisibleInKitchen"
        :is-open="categoryModalOpen"
        :is-editing="isEditing"
        :limit-error="subscriptionLimitError"
        @save="saveCategory"
        @cancel="closeModal"
      />
    </div>
  </MainLayout>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { usePermissions } from '@/composables/usePermissions';
import { useCategoryManagement } from '@/composables/useCategoryManagement';
import { platformFeatures } from '@/utils/platform';
import MainLayout from '@/components/layout/MainLayout.vue';
import PageHeader from '@/components/layout/PageHeader.vue';
import SearchBar from '@/components/ui/SearchBar.vue';
import MenuSubNavbar from '@/components/menu/MenuSubNavbar.vue';
import CategoryList from '@/components/categories/CategoryList.vue';
import CategoryModal from '@/components/categories/CategoryModal.vue';
import BaseButton from '@/components/common/BaseButton.vue';
import ViewModeToggle from '@/components/common/ViewModeToggle.vue';

const { canEditCategories } = usePermissions();
const { t } = useI18n();

// View mode state
const viewMode = ref<'list' | 'card'>('card');

// Use the composable for all category management logic
const {
  categoriesLoading,
  categoryModalOpen,
  categoryFormName,
  categoryFormDescription,
  categoryFormVisibleInKitchen,
  subscriptionLimitError,
  categorySearch,
  menuCategoriesDetailed,
  isEditing,
  initialize,
  setCategorySearch,
  openModal,
  closeModal,
  saveCategory,
  deleteCategory
} = useCategoryManagement();

// Add/remove body class for scrollbar control
onMounted(() => {
  initialize();
  document.body.classList.add('menu-view-active');
});

onUnmounted(() => {
  document.body.classList.remove('menu-view-active');
});
</script>
